import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import BaseTooltip from 'components/Tooltips/BaseTooltip';
import AudioPreview from 'components/UI/AudioPreview';
import TextToggle from 'components/UI/TextToggle';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/DeviceControls.json';
import Hls from 'hls.js';
import useSirenAudio from 'hooks/useSirenAudio';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { IoVolumeHigh, IoVolumeMute } from 'react-icons/io5';
import { PiPencilLineFill } from 'react-icons/pi';
import apiClient from 'services/_infra/apiClient';

const AUDIO_IDLE_CAVEAT =
  'Audio selection can be previewed, but it will not activate while the device is set to Idle.';

/**
 * @param {{device: object, displayMessage?: {type: string, message: string}, on?: (event: string, data: object), sending?: boolean}}
 */
export default function DeviceControls({
  device,
  devices,
  displayMessage,
  audioOverride,
  on,
  sending = null,
  audioManagerButtonEnabled = true,
  stopLiveAudioSignal = null,
  compact = false,
  // Group mode: false when every siren already has its own file in the rows
  // below the controls, so an alert may go without a group-wide file.
  audioRequired = true,
}) {
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      require('hooks/useRenderTracker').default('DeviceControls');
    }
  } catch (e) {}
  const { allAudio, universalAudio, sirenAudio, groupAudio } = useSirenAudio(devices || device);
  // A group may pick ANY file in the library: on Save the backend associates it
  // with every siren in the group that lacks it (Mike, 2026-08-28: "if we
  // select a file to send we should associate that file with all of them
  // automatically"). Files not yet on every siren say so in the list.
  const audioList = useMemo(
    () => (devices ? [...allAudio] : [...universalAudio, ...sirenAudio]),
    [devices, allAudio, universalAudio, sirenAudio],
  );
  const onEverySiren = useMemo(() => new Set((groupAudio || []).map((a) => a?.id)), [groupAudio]);
  const perms = usePerms();
  // Several DeviceControls can share a page, so the caveat's id has to be per-instance.
  const instanceId = useId();
  const audioIdleCaveatId = `${instanceId}audio-idle-caveat`;

  const audioRef = useRef(null);
  const hlsRef = useRef(null);

  const [type, setType] = useState('');
  const [changes, setChanges] = useState(false);
  const [removeMe, setRemoveMe] = useState(false);
  const [liveAudio, setLiveAudio] = useState(false);
  /**
   * Why Listen Live cannot play, when it cannot.
   *
   * The button used to flip to "playing" whatever happened, and every failure
   * went only to `console.error` — so a device with no camera looked exactly
   * like a device playing silence. A demo siren has no camera by design, so
   * that would have been its permanent state.
   *
   * The control stays VISIBLE either way. It just stops pretending.
   */
  const [liveAudioUnavailable, setLiveAudioUnavailable] = useState(null);

  // Track changes only for user-initiated edits (avoid false "unsaved" on init).
  const setData = (newState, trackChanges = true) => {
    on('update', newState);
    if (trackChanges) setChanges(true);
  };

  useEffect(() => {
    // Log device and context info on modal open
    // console.log('[DeviceControls] modal opened', {
    //   device,
    //   devices,
    //   deviceType: device?.device_type || '',
    // });

    on('open');

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      on('close');
    };
  }, []);

  useEffect(() => {
    if (stopLiveAudioSignal == null) return;
    stopLiveAudio();
  }, [stopLiveAudioSignal]);

  useEffect(() => {
    if (!device && !devices) return;
    if (audioOverride)
      setData(
        {
          ...device,
          status: audioOverride?.relay_id || true,
          data: [{ ...(device?.data?.[0] || {}), status: 1 }],
          audioIndex: -1,
          audio: audioOverride,
        },
        false,
      );
    else
      setData(
        {
          ...device,
          status: false,
          data: [{ ...(device?.data?.[0] || {}), status: 1 }],
          audioIndex: null,
          audio: null,
        },
        false,
      );
  }, [audioOverride]);

  useEffect(() => {
    if (!device && !devices) return;
    setType(device?.device_type || '');

    let newDevice = { ...device };
    let changed = false;

    if (device?.status === undefined || device?.toggle_switch_text === undefined) {
      newDevice = {
        ...newDevice,
        toggle_switch_text: device?.data?.[0]?.toggle_switch ? 'On' : 'Off',
        status: device?.status || !!device?.data?.[0]?.status,
      };
      changed = true;
    }

    if (
      device?.data?.[0]?.command > 0 &&
      device?.device_type.includes('Siren') &&
      device?.audioIndex === undefined &&
      audioList
    ) {
      const audio = audioList.findIndex((audio) => audio.relay_id === device?.data?.[0].command);

      if (audio >= 0) {
        newDevice = {
          ...newDevice,
          audioIndex: audio,
          audio: audioList[audio],
        };
        changed = true;
      }
    }

    if (changed) setData(newDevice, false);
  }, [device, audioList]);

  const toggleSirenLiveAudio = async () => {
    if (!!devices) return;

    if (liveAudio) stopLiveAudio();
    else startLiveAudio();
  };

  const getLiveAudioDeviceId = () => {
    if (device?.id) {
      return device?.id;
    }

    return null;
  };

  /**
   * Why this device cannot serve live audio, or null when it can try.
   *
   * Listen Live is the AUDIO TRACK OF A PAIRED CAMERA, not something the siren
   * itself serves. With no camera the backend answers 404 and there is nothing
   * to play, so there is no reason to open a connection and wait to find out.
   *
   * A demo siren is named explicitly rather than lumped in with "no camera".
   * It may well be paired with a camera for the sake of the demonstration, and
   * it still must never open a stream. The word is DEMO, never "simulated" and
   * never against "real" (Mike, 2026-09-01): a deployment is all real or all
   * demo, so the line says what this device cannot do, not which fleet it is.
   */
  // Greyed out, not hidden: the control sits where an integrated siren's sits.
  const isDemoDevice = Number(device?.is_simulated) === 1;
  const demoLiveAudioMessage = 'Live audio is not available for this demo device.';

  const liveAudioBlockedReason = () => {
    if (isDemoDevice) return demoLiveAudioMessage;
    if (!device?.camera) return 'No camera is paired with this device, so there is no live audio.';
    return null;
  };

  const failLiveAudio = (reason, error) => {
    if (error) console.error('Error in DeviceControls:Audio:', error);
    stopLiveAudio();
    setLiveAudio(false);
    setLiveAudioUnavailable(reason);
  };

  const startLiveAudio = () => {
    const liveAudioDeviceId = getLiveAudioDeviceId();
    if (!liveAudioDeviceId || !audioRef.current) {
      return;
    }

    // Never open a connection we already know cannot succeed.
    const blocked = liveAudioBlockedReason();
    if (blocked) {
      setLiveAudioUnavailable(blocked);
      setLiveAudio(false);
      return;
    }

    const url = `${process.env.PUBLIC_API_URL}/v2/integrations/device/siren/${encodeURIComponent(liveAudioDeviceId)}/audio-stream/index.m3u8`;

    stopLiveAudio();
    setLiveAudioUnavailable(null);

    if (audioRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      audioRef.current.src = url;
      audioRef.current.play().catch((error) => {
        failLiveAudio('Live audio could not be started.', error);
      });
      setLiveAudio(true);
      return;
    }

    const hls = new Hls();
    hlsRef.current = hls;
    hls.loadSource(url);
    hls.attachMedia(audioRef.current);
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      audioRef.current?.play().catch((error) => {
        failLiveAudio('Live audio could not be started.', error);
      });
    });
    hls.on(Hls.Events.ERROR, (_event, data) => {
      // Only a fatal error means the stream is gone. hls.js reports recoverable
      // ones constantly during normal playback, and treating those as failure
      // would make a working stream flicker "unavailable".
      if (data?.fatal) {
        failLiveAudio('The live audio stream is not available for this device.', data);
      } else {
        console.error('Error in DeviceControls:Hls:', data);
      }
    });
    setLiveAudio(true);
  };

  const stopLiveAudio = () => {
    hlsRef.current?.destroy();
    hlsRef.current = null;

    audioRef.current?.pause();
    audioRef.current?.removeAttribute('src');
    audioRef.current?.load();

    // Cleanup any lingering live audio stream
    const liveAudioDeviceId = getLiveAudioDeviceId();
    if (liveAudioDeviceId) {
      apiClient.delete(
        `/v2/integrations/device/siren/${encodeURIComponent(liveAudioDeviceId)}/audio-stream`,
      );
    }

    setLiveAudio(false);
  };

  const renderComponent = (component) => {
    switch (component.type) {
      case 'Label':
        return renderLabel(component);
      case 'TextToggle':
        return renderTextToggle(component);
      case 'AudioTextToggle':
        return renderSirenTextToggle(component);
      case 'textarea':
        return renderTextArea(component);
      case 'interval-input':
        return renderIntervalInput(component);
      case 'slider':
        return renderSlider(component);
      case 'send-status-to':
        return renderExternalShare();
      case 'unsaved-changes-label':
        return renderUnsavedChanges();
      case 'save-button':
        return renderSaveButton();
      case 'audio-dropdown':
        return renderAudioDropdown();
      case 'maintenance-override':
        return renderMaintenanceOverride();
      case 'display-message':
        return renderDisplayMessage();
      default:
        return null;
    }
  };

  const renderLabel = (component) => {
    const label = component.label.replace(/{(.*?)}/g, (_, key) => {
      return device[key] ?? '';
    });
    return (
      <div>
        <label>{label}</label>
        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderTextToggle = (component) => {
    let properties = {
      disabled:
        (device?.kill_switch || device?.data?.[0]?.status < 0) && !device?.maintenanceOverride,
      options: component.props.options,
      value: !!device[component.props.value],
      setValue: (value) => {
        if (component.props.setValue === 'status')
          setData({
            ...device,
            [component.props.setValue]: value,
            data: [{ ...(device?.data?.[0] || {}), status: value ? 1 : 0 }],
          });
        else
          setData({
            ...device,
            [component.props.setValue]: value,
          });
      },
    };

    return (
      <div>
        <div className="d-flex flex-column flex-lg-row justify-content-between">
          <div className="flex-fill flex-bases-0">
            <label>{component.label}</label>
          </div>
          <div className="flex-fill flex-bases-0">
            <TextToggle {...properties} />
          </div>
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderSirenTextToggle = (component) => {
    const liveAudioDeviceId = getLiveAudioDeviceId();
    let properties = {
      disabled:
        (device?.kill_switch || device?.data?.[0]?.status < 0) && !device?.maintenanceOverride,
      options: component.props.options,
      value: !!device[component.props.value],
      setValue: (value) => {
        if (component.props.setValue === 'status')
          setData({
            ...device,
            [component.props.setValue]: value,
            data: [{ ...(device?.data?.[0] || {}), status: value ? 1 : 0 }],
          });
        else
          setData({
            ...device,
            [component.props.setValue]: value,
          });
      },
    };

    return (
      <div>
        <div
          className={
            compact
              ? 'd-flex flex-column align-items-stretch gap-2'
              : 'd-flex flex-column flex-lg-row justify-content-between align-items-stretch align-items-lg-center gap-2 gap-lg-0'
          }
        >
          <div
            className={
              compact
                ? 'd-flex flex-column gap-2'
                : 'd-flex flex-column flex-lg-row flex-fill flex-basis-0 justify-content-between align-items-stretch align-items-lg-center'
            }
          >
            <div className="flex-fill flex-basis-0">
              <label>{component.label}</label>
            </div>
            <div className="flex-fill">
              <TextToggle {...properties} />
            </div>
          </div>

          <div
            className={`d-flex flex-wrap gap-2 ${
              compact ? 'justify-content-end' : 'flex-fill flex-basis-0 justify-content-lg-end'
            }`}
          >
            {/*
              The Listen Live control stays VISIBLE even when there is no feed,
              so the interface reads the same as an integrated siren. What it
              must not do is claim to be playing — see `liveAudioUnavailable`.
            */}
            {!devices &&
              liveAudioDeviceId &&
              (isDemoDevice ? (
                <BaseTooltip
                  id="disabled-listen-live-tooltip"
                  text="No VMS devices are linked to the selected Siren/Giant Voice devices"
                  placement="top"
                >
                  <span className="d-block">
                    <Button
                      variant="primary"
                      onClick={toggleSirenLiveAudio}
                      disabled={isDemoDevice}
                      aria-label={liveAudio ? 'Stop listening' : 'Listen in'}
                    >
                      {!liveAudio && <IoVolumeMute size={25} />}
                      {liveAudio && <IoVolumeHigh size={25} />}
                    </Button>
                  </span>
                </BaseTooltip>
              ) : (
                <Button
                  variant="primary"
                  onClick={toggleSirenLiveAudio}
                  disabled={isDemoDevice}
                  title={
                    liveAudioUnavailable
                      ? liveAudioUnavailable
                      : liveAudio
                        ? 'Stop listening'
                        : 'Listen in'
                  }
                  aria-label={liveAudio ? 'Stop listening' : 'Listen in'}
                >
                  {!liveAudio && <IoVolumeMute size={25} />}
                  {liveAudio && <IoVolumeHigh size={25} />}
                </Button>
              ))}

            <Button variant="primary" onClick={() => on('cancel-audio', {})}>
              Cancel Audio
            </Button>
          </div>
        </div>

        {liveAudioUnavailable && (
          <div className="text-muted small mt-1 text-lg-end">{liveAudioUnavailable}</div>
        )}

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderTextArea = (component) => {
    return (
      <div>
        <div
          className={`d-flex flex-column ${compact ? '' : 'flex-lg-row'} align-items-baseline gap-2`}
        >
          <label>{component?.label}</label>
          <Form.Control
            as="textarea"
            className="border border-white rounded"
            placeholder={component?.props?.placeholder}
            onChange={(e) =>
              setData({
                ...device,
                [component?.props?.onChange]: e.target.value,
              })
            }
          />
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderIntervalInput = (component) => {
    if (!device?.status) return null;

    const {
      text,
      disableValue,
      maxValue,
      minValue,
      value,
      repeatValue,
      repeatForeverLabel,
      repeatForeverValue,
    } = component.props;

    return (
      <div>
        <div
          className={`d-flex flex-column ${compact ? '' : 'flex-lg-row align-items-lg-center'} align-items-stretch gap-2`}
        >
          <Button
            variant="link"
            className="position-relative p-0 text-white text-decoration-none"
            onClick={() => setRemoveMe(!removeMe)}
          >
            <div className="d-flex gap-1 align-items-center">
              <FaInfoCircle /> <label>{component?.label}</label>
            </div>

            <div
              className={`${
                removeMe ? 'd-block' : 'd-none'
              } position-absolute bg-white text-black rounded-2 p-3 z-2 shadow`}
              style={{
                top: 'calc(100% + 0.5rem)',
                left: 0,
                width: 'clamp(280px, 34vw, 440px)',
                maxWidth: 'min(440px, calc(100vw - 4rem))',
                maxHeight: 'min(320px, calc(100vh - 12rem))',
                overflowY: 'auto',
                overflowX: 'hidden',
                whiteSpace: 'normal',
              }}
            >
              <p className="m-0 text-start">
                This setting controls how often the event should automatically repeat, measured in
                seconds. It's especially useful for scenarios where an action, such as a siren
                activation or audible alert, needs to occur on a recurring loop. For example, if
                you're triggering a 30-second siren tone, you can set it to repeat continuously
                every 30 seconds, every 15 minutes, or even once every few hours depending on your
                operational needs. Use this field to customize the spacing between each activation
                cycle. The system will re-trigger the event at the defined interval as long as the
                condition remains true and the Global Action is active. Valid input range: 30 to
                7200 seconds (i.e., from every 30 seconds to every 2 hours).
              </p>
            </div>
          </Button>

          <div
            className={compact ? 'w-100' : 'w-25'}
            style={compact ? { maxWidth: 180 } : undefined}
          >
            <TextToggle
              options={['Off', 'On']}
              value={device[value] !== undefined && device[value] !== disableValue}
              setValue={(_value) => {
                if (_value)
                  setData({
                    ...device,
                    [value]: minValue || 0,
                  });
                else
                  setData({
                    ...device,
                    [value]: disableValue || 0,
                  });
              }}
              invert
            />
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <Form.Control
              aria-label={component?.label || 'Repeat interval'}
              disabled={device[value] === disableValue}
              type="number"
              value={device[value] || 0}
              onChange={(e) =>
                setData({
                  ...device,
                  [value]: e.target.value,
                })
              }
              onBlur={() =>
                setData({
                  ...device,
                  [value]: Math.max(minValue, Math.min(maxValue, device[value])),
                })
              }
            />
            {text && <p className="m-0">{text}</p>}
          </div>
        </div>

        {component.countEnabled && (
          <div
            className={`d-flex ${
              compact
                ? 'flex-wrap align-items-end'
                : 'flex-column flex-lg-row align-items-stretch align-items-lg-center'
            } gap-2 mt-2`}
          >
            <div>
              <label>{component.countLabel}</label>
            </div>

            <div>
              {device[repeatValue] !== repeatForeverValue && (
                <Form.Control
                  aria-label={component.countLabel || 'Repeat count'}
                  disabled={device[value] === disableValue}
                  type="number"
                  value={device[repeatValue] || 0}
                  onChange={(e) =>
                    setData({
                      ...device,
                      [repeatValue]: e.target.value,
                    })
                  }
                  onBlur={() =>
                    setData({
                      ...device,
                      [repeatValue]: Math.max(device[repeatValue], 1),
                    })
                  }
                />
              )}
            </div>

            <div className="d-flex align-items-center gap-2 pb-1">
              <Form.Check
                type="checkbox"
                disabled={device[value] === disableValue}
                checked={device[repeatValue] === repeatForeverValue}
                onChange={(e) =>
                  setData({
                    ...device,
                    [repeatValue]: e.target.checked ? repeatForeverValue : 0,
                  })
                }
              />
              <p className="m-0">{repeatForeverLabel}</p>
            </div>
          </div>
        )}

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderSlider = (component) => {
    if (!device?.status) return null;

    const { value, min, max } = component.props;
    const sliderValue = device?.[value] ?? max;

    return (
      <div>
        <div
          className={`d-flex flex-column ${compact ? '' : 'flex-lg-row align-items-lg-center'} gap-4`}
        >
          <label className="mb-0 flex-shrink-0" style={{ minWidth: 96 }}>
            {component.label} {sliderValue}%
          </label>
          <Form.Range
            type="range"
            className="w-100"
            style={{ maxWidth: 240 }}
            value={sliderValue}
            min={min}
            max={max}
            onChange={(e) =>
              setData({
                ...device,
                [value]: Math.min(Math.max(e.target.value, min), max),
              })
            }
          />
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderExternalShare = () => {
    //TODO: If this gets re-enabled it will need to be bootstrapped.
    return null;
  };

  const renderUnsavedChanges = () => {
    return (
      <div className={`${changes ? 'd-block' : 'd-none'}`}>
        *You have unsaved changes, Discard or Save to continue*
      </div>
    );
  };

  const renderSaveButton = () => {
    return (
      <div className={`d-flex flex-fill justify-content-end ${compact ? 'p-2' : ''}`}>
        {sending && (
          <div>
            <WidgetLoadingAnimation disableText />
          </div>
        )}
        <Button
          className={compact ? 'px-3 py-2' : 'p-3'}
          disabled={
            device?.data?.[0]?.status < 0 ||
            (device?.kill_switch && !device?.maintenanceOverride) ||
            (audioRequired && device?.status && device?.audioIndex < 0 && !audioOverride) ||
            sending
          }
          onClick={() => on('submit', device)}
        >
          Save
        </Button>
      </div>
    );
  };

  // Helper function for renderAudioDropdown. `-1` is the "no selection" sentinel, and
  // BaseSelect's clear also lands here as null.
  const updateSelectedAudio = (optionValue) => {
    const value = optionValue == null ? -1 : Number(optionValue);
    const list = audioOverride ? [audioOverride, ...(audioList || [])] : audioList || [];

    if (value === -1) {
      setData({
        ...device,
        audioIndex: -1,
        audio: null,
      });
    } else {
      const selectedAudio = list?.[value] || null;

      setData({
        ...device,
        audioIndex: value,
        audio: selectedAudio,
      });
    }
  };

  const renderAudioDropdown = () => {
    const list = audioOverride ? [audioOverride, ...(audioList || [])] : audioList || [];
    const hasAudioOptions = list.length > 0;
    const audioDropdownInactive = !device?.status;
    const noSelectionLabel = hasAudioOptions
      ? '-- Select Audio --'
      : devices
        ? '-- No audio files in the library yet --'
        : '-- No Audio Available --';
    // Index is the value, matching the audioIndex the parent stores; -1 means "nothing picked".
    const audioOptions = [
      { value: -1, label: noSelectionLabel },
      ...list.map((audio, index) => ({
        value: index,
        label: `${audio?.displayName || audio?.name || `Audio ${index + 1}`}${
          devices && audio?.id != null && !onEverySiren.has(audio.id)
            ? ' — will be added to the sirens that lack it'
            : ''
        }`,
      })),
    ];

    return (
      <div>
        <div className={`d-flex flex-column ${compact ? '' : 'flex-lg-row'} gap-2`}>
          {/* react-select takes no `title`, so the wrapper carries the idle tooltip for the
              mouse; the select points at the hidden copy above so it is announced on focus. */}
          <div
            className="flex-grow-1"
            title={audioDropdownInactive ? AUDIO_IDLE_CAVEAT : undefined}
          >
            {audioDropdownInactive && (
              <span className="visually-hidden" id={audioIdleCaveatId}>
                {AUDIO_IDLE_CAVEAT}
              </span>
            )}
            <BaseSelect
              isSearchable
              describedById={audioDropdownInactive ? audioIdleCaveatId : undefined}
              styles={
                audioDropdownInactive
                  ? {
                      control: (base) => ({
                        ...base,
                        backgroundColor: '#6c757d',
                        borderColor: '#ffffff',
                        color: '#ced4da',
                        opacity: 1,
                      }),
                    }
                  : undefined
              }
              selectProps={{
                'aria-label': 'Select Audio',
                options: audioOptions,
                value: findSelectOption(audioOptions, device?.audioIndex ?? -1),
                isDisabled: !hasAudioOptions,
                placeholder: noSelectionLabel,
                onChange: (option) => updateSelectedAudio(option?.value),
              }}
            />
          </div>

          {/* TODO: Re write AudioPreview component */}
          {device?.audio?.id && <AudioPreview tone={device?.audio} />}

          {audioManagerButtonEnabled &&
            (perms.testPermission([], ['CreateSirenAudioFiles']) ||
              perms.testPermission([], ['Edit-DeleteSirenAudioFiles'])) &&
            device?.camera && (
              <Button
                className="p-2 px-4"
                onClick={() => {
                  on('audio-manager', {});
                  stopLiveAudio();
                }}
              >
                <PiPencilLineFill />
              </Button>
            )}
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderMaintenanceOverride = () => {
    if (perms.isMaintenanceOperator() && (!device?.kill_switch || device?.data?.[0]?.status > -1))
      return (
        <div className="d-flex gap-2 align-items-baseline">
          <Form.Check
            type="checkbox"
            checked={!!device?.maintenanceOverride}
            onChange={(e) => {
              setData({ ...device, maintenanceOverride: e.target.checked });
            }}
          />
          <p>
            Override <span className="text-warning">Maintenance</span>?
          </p>
        </div>
      );
  };

  const renderDisplayMessage = () => {
    if (displayMessage)
      return (
        <p className={displayMessage?.type === 'error' ? 'text-error' : 'text-success'}>
          {displayMessage.message}
        </p>
      );
  };

  if (!device && !devices) return null;

  return (
    <div className={`container-fluid d-flex flex-column gap-2 ${compact ? 'px-0' : ''}`}>
      {layout[type.toLowerCase()]?.components?.map((component, index) => (
        <div key={`${component.type}-${index}`}>{renderComponent(component)}</div>
      ))}
      <audio className="hidden" ref={audioRef} autoPlay />
    </div>
  );
}
